import { officeSalesSessionConstants } from '../actions/constants'

const initialState = {
    sessions: [],
    activeSession: null,
    listOfficeId: '',
    loading: false,
    creating: false,
    updating: false,
    inviteLoading: false,
    cartUpdating: false,
    convertingToOrder: false,
    lastConvertedOrder: null,
    error: null,
    lastInviteToken: '',
    lastInviteExpiresAt: null,
    pagination: null,
}

const getSessionTimestamp = (session) => {
    const timestamp = session?.updatedAt ? Date.parse(session.updatedAt) : 0
    return Number.isFinite(timestamp) ? timestamp : 0
}

const isSameSession = (a, b) => {
    if (!a?.sessionId || !b?.sessionId) return false

    const aOfficeId = String(a?.officeId?._id || a?.officeId || '')
    const bOfficeId = String(b?.officeId?._id || b?.officeId || '')

    return String(a.sessionId).toUpperCase() === String(b.sessionId).toUpperCase() &&
        (!aOfficeId || !bOfficeId || aOfficeId === bOfficeId)
}

const pickFresherSession = (current, incoming) => {
    if (!current) return incoming || null
    if (!incoming) return current
    if (!isSameSession(current, incoming)) return incoming

    const currentTimestamp = getSessionTimestamp(current)
    const incomingTimestamp = getSessionTimestamp(incoming)

    if (currentTimestamp > 0 && incomingTimestamp > 0 && incomingTimestamp < currentTimestamp) {
        return current
    }

    return incoming
}

const upsertSession = (sessions, session) => {
    if (!session?.sessionId) return sessions

    const exists = sessions.some((item) => isSameSession(item, session))

    if (!exists) {
        return [session, ...sessions]
    }

    return sessions.map((item) => (
        isSameSession(item, session) ? pickFresherSession(item, session) : item
    ))
}

const mergeSession = (current, patch) => {
    if (!current) return patch || null
    if (!patch) return current

    return {
        ...current,
        ...patch,
        catalogState: patch.catalogState
            ? {
                ...(current.catalogState || {}),
                ...patch.catalogState,
            }
            : current.catalogState,
        calc3dState: patch.calc3dState
            ? {
                ...(current.calc3dState || {}),
                ...patch.calc3dState,
            }
            : current.calc3dState,
        orderDraftState: patch.orderDraftState
            ? {
                ...(current.orderDraftState || {}),
                ...patch.orderDraftState,
            }
            : current.orderDraftState,
    }
}

const officeSalesSessionReducer = (state = initialState, action) => {
    switch (action.type) {
        case officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_REQUEST:
            return {
                ...state,
                listOfficeId: String(action.payload?.officeId || ''),
                loading: true,
                error: null,
            }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_REQUEST:
            return {
                ...state,
                creating: true,
                error: null,
            }

        case officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
            }

        case officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_REQUEST:
        case officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_REQUEST:
        case officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_REQUEST:
            return {
                ...state,
                updating: true,
                error: null,
            }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_REQUEST:
            return {
                ...state,
                inviteLoading: true,
                error: null,
                lastInviteToken: '',
                lastInviteExpiresAt: null,
    pagination: null,
            }

        case 'OFFICE_SALES_SESSION_CART_UPDATE_REQUEST':
            return {
                ...state,
                cartUpdating: true,
                error: null,
            }

        case 'OFFICE_SALES_SESSION_CONVERT_ORDER_REQUEST':
            return {
                ...state,
                convertingToOrder: true,
                error: null,
            }

        case officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_SUCCESS: {
            const responseOfficeId = String(action.payload?.officeId || '')

            if (
                responseOfficeId &&
                state.listOfficeId &&
                responseOfficeId !== state.listOfficeId
            ) {
                return state
            }

            return {
                ...state,
                listOfficeId: responseOfficeId || state.listOfficeId,
                loading: false,
                sessions: action.payload.append
                    ? [...state.sessions, ...(action.payload.sessions || [])].filter((session, index, list) => (
                        list.findIndex((item) => isSameSession(item, session)) === index
                    ))
                    : (action.payload.sessions || []),
                pagination: action.payload.pagination || null,
            }
        }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_SUCCESS:
            return {
                ...state,
                creating: false,
                activeSession: pickFresherSession(state.activeSession, action.payload.session),
                sessions: upsertSession(state.sessions, action.payload.session),
            }

        case officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_SUCCESS:
            return {
                ...state,
                loading: false,
                activeSession: pickFresherSession(state.activeSession, action.payload.session),
                sessions: upsertSession(state.sessions, action.payload.session),
            }

        case officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_SUCCESS:
        case officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_SUCCESS:
        case officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_SUCCESS:
            return {
                ...state,
                updating: false,
                activeSession: pickFresherSession(state.activeSession, action.payload.session),
                sessions: upsertSession(state.sessions, action.payload.session),
            }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_SUCCESS:
            return {
                ...state,
                inviteLoading: false,
                activeSession: pickFresherSession(state.activeSession, action.payload.session),
                sessions: upsertSession(state.sessions, action.payload.session),
                lastInviteToken: action.payload.inviteToken || '',
                lastInviteExpiresAt: action.payload.inviteExpiresAt || null,
            }

        case 'OFFICE_SALES_SESSION_CART_UPDATE_SUCCESS': {
            const session = action.payload.session

            return {
                ...state,
                cartUpdating: false,
                activeSession: pickFresherSession(state.activeSession, session),
                sessions: session ? upsertSession(state.sessions, session) : state.sessions,
            }
        }

        case 'OFFICE_SALES_SESSION_CONVERT_ORDER_SUCCESS': {
            const session = action.payload.session

            return {
                ...state,
                convertingToOrder: false,
                lastConvertedOrder: action.payload.order || null,
                activeSession: pickFresherSession(state.activeSession, session),
                sessions: session ? upsertSession(state.sessions, session) : state.sessions,
            }
        }

        case officeSalesSessionConstants.SET_ACTIVE_OFFICE_SALES_SESSION: {
            const incomingSession = action.payload?.session || null

            return {
                ...state,
                activeSession: incomingSession
                    ? pickFresherSession(state.activeSession, incomingSession)
                    : null,
            }
        }

        case officeSalesSessionConstants.PATCH_ACTIVE_OFFICE_SALES_SESSION: {
            const activeSession = mergeSession(state.activeSession, action.payload.patch)

            return {
                ...state,
                activeSession,
                sessions: activeSession ? upsertSession(state.sessions, activeSession) : state.sessions,
            }
        }

        case officeSalesSessionConstants.CLEAR_ACTIVE_OFFICE_SALES_SESSION:
            return {
                ...state,
                activeSession: null,
            }

        case officeSalesSessionConstants.LIST_OFFICE_SALES_SESSIONS_FAILURE: {
            const responseOfficeId = String(action.payload?.officeId || '')

            if (
                responseOfficeId &&
                state.listOfficeId &&
                responseOfficeId !== state.listOfficeId
            ) {
                return state
            }

            return {
                ...state,
                loading: false,
                error: action.payload.error,
            }
        }

        case officeSalesSessionConstants.GET_OFFICE_SALES_SESSION_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload.error,
            }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_FAILURE:
            return {
                ...state,
                creating: false,
                error: action.payload.error,
            }

        case officeSalesSessionConstants.UPDATE_OFFICE_SALES_SESSION_FAILURE:
        case officeSalesSessionConstants.CLOSE_OFFICE_SALES_SESSION_FAILURE:
        case officeSalesSessionConstants.REOPEN_OFFICE_SALES_SESSION_FAILURE:
            return {
                ...state,
                updating: false,
                error: action.payload.error,
            }

        case officeSalesSessionConstants.CREATE_OFFICE_SALES_SESSION_INVITE_FAILURE:
            return {
                ...state,
                inviteLoading: false,
                error: action.payload.error,
            }

        case 'OFFICE_SALES_SESSION_CART_UPDATE_FAILURE':
            return {
                ...state,
                cartUpdating: false,
                error: action.payload.error,
            }

        case 'OFFICE_SALES_SESSION_CONVERT_ORDER_FAILURE':
            return {
                ...state,
                convertingToOrder: false,
                error: action.payload.error,
            }

        default:
            return state
    }
}

export default officeSalesSessionReducer
